import posthog from 'posthog-js';

// Strip undefined/null so PostHog properties stay clean
function p(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null)
  );
}

// ─── Auth Funnel ──────────────────────────────────────────────────────────────

export function trackSignupStarted({ method }) {
  posthog.capture('signup_started', p({ method }));
}

export function trackSignupCompleted({ method, requires_email_verification }) {
  posthog.capture('signup_completed', p({ method, requires_email_verification }));
}

export function trackSignupFailed({ method, error_message }) {
  posthog.capture('signup_failed', p({ method, error_message }));
}

export function trackLoginCompleted({ method }) {
  posthog.capture('login_completed', p({ method }));
}

export function trackLoginFailed({ method, error_message }) {
  posthog.capture('login_failed', p({ method, error_message }));
}

export function trackLogout({ session_duration_minutes } = {}) {
  posthog.capture('logout', p({ session_duration_minutes }));
}

// ─── Conversion Funnel ────────────────────────────────────────────────────────

export function trackPricingViewed({ source, current_plan, billing_toggle }) {
  // source: 'navbar' | 'upgrade_modal' | 'locked_widget' | 'direct'
  posthog.capture('pricing_viewed', p({ source, current_plan, billing_toggle }));
}

export function trackBillingToggled({ interval }) {
  posthog.capture('billing_interval_toggled', p({ interval }));
}

export function trackPlanClicked({ plan_id, interval, action_type, current_plan }) {
  // action_type: 'upgrade' | 'downgrade' | 'get_started'
  posthog.capture('plan_cta_clicked', p({ plan_id, interval, action_type, current_plan }));
}

export function trackCheckoutStarted({ plan_id, interval }) {
  posthog.capture('checkout_started', p({ plan_id, interval }));
}

export function trackPlanChanged({ from_plan, to_plan, interval, action }) {
  // action: 'upgraded' | 'scheduled' | 'downgraded'
  posthog.capture('plan_changed', p({ from_plan, to_plan, interval, action }));
}

export function trackCheckoutCompleted({ plan_id, session_id }) {
  posthog.capture('checkout_completed', p({ plan_id, session_id }));
}

// ─── Paywall / Upgrade Prompts ────────────────────────────────────────────────

export function trackUpgradeModalShown({ feature_key, limit_used, limit_max }) {
  posthog.capture('upgrade_modal_shown', p({ feature_key, limit_used, limit_max }));
}

export function trackUpgradeModalDismissed({ feature_key }) {
  posthog.capture('upgrade_modal_dismissed', p({ feature_key }));
}

export function trackUpgradeModalCTAClicked({ feature_key }) {
  posthog.capture('upgrade_modal_cta_clicked', p({ feature_key }));
}

export function trackLockedWidgetSeen({ widget_title, required_plan }) {
  posthog.capture('locked_widget_seen', p({ widget_title, required_plan }));
}

export function trackLockedWidgetUpgradeClicked({ widget_title, required_plan }) {
  posthog.capture('locked_widget_upgrade_clicked', p({ widget_title, required_plan }));
}

// ─── Trade Operations ─────────────────────────────────────────────────────────

export function trackTradeAdded({ symbol, direction, has_screenshots, screenshot_count }) {
  posthog.capture('trade_added', p({ symbol, direction, has_screenshots, screenshot_count }));
}

export function trackTradeEdited({ symbol }) {
  posthog.capture('trade_edited', p({ symbol }));
}

export function trackTradeDeleted() {
  posthog.capture('trade_deleted');
}

export function trackTradeImportStarted({ row_count }) {
  posthog.capture('trade_import_started', p({ row_count }));
}

export function trackTradeImportCompleted({ imported_count, skipped_count }) {
  posthog.capture('trade_import_completed', p({ imported_count, skipped_count }));
}

export function trackTradeImportFailed({ error_type }) {
  posthog.capture('trade_import_failed', p({ error_type }));
}

// ─── Journal (Chronicle) ──────────────────────────────────────────────────────

export function trackJournalSessionStarted({ mode }) {
  posthog.capture('journal_session_started', p({ mode }));
}

export function trackJournalMessageSent({ message_index }) {
  posthog.capture('journal_message_sent', p({ message_index }));
}

export function trackJournalSessionCompleted({ message_count }) {
  posthog.capture('journal_session_completed', p({ message_count }));
}

export function trackJournalSummaryViewed() {
  posthog.capture('journal_summary_viewed');
}

// ─── Sage AI Coach ────────────────────────────────────────────────────────────

export function trackAICoachQuerySent({ query_category, query_length }) {
  // query_category: 'patterns' | 'coaching' | 'suggestions' | 'plans' | 'custom'
  posthog.capture('ai_coach_query_sent', p({ query_category, query_length }));
}

export function trackAICoachResponseReceived({ query_category, response_type }) {
  posthog.capture('ai_coach_response_received', p({ query_category, response_type }));
}

// ─── Insight Lab ──────────────────────────────────────────────────────────────

export function trackInsightLabSessionCreated() {
  posthog.capture('insight_lab_session_created');
}

export function trackInsightLabMessageSent({ session_message_index }) {
  posthog.capture('insight_lab_message_sent', p({ session_message_index }));
}

// ─── Blueprint Playbook ───────────────────────────────────────────────────────

export function trackPlaybookCreated() {
  posthog.capture('playbook_created');
}

export function trackPlaybookRuleAdded() {
  posthog.capture('playbook_rule_added');
}

export function trackPlaybookAnalyticsViewed() {
  posthog.capture('playbook_analytics_viewed');
}

// ─── Cortex Psychology ────────────────────────────────────────────────────────

export function trackCortexTabViewed({ tab }) {
  posthog.capture('cortex_tab_viewed', p({ tab }));
}

export function trackCortexRefreshed() {
  posthog.capture('cortex_data_refreshed');
}

// ─── Performance / Edge Analytics ────────────────────────────────────────────

export function trackPerformanceFilterChanged({ filter_type, value }) {
  posthog.capture('performance_filter_changed', p({ filter_type, value }));
}

// ─── Profile ──────────────────────────────────────────────────────────────────

export function trackProfileUpdated({ fields_changed }) {
  posthog.capture('profile_updated', p({ fields_changed }));
}
